"use client";

import {
  useWalletPortfolioContext,
  usePortfolioFiat,
} from "../../providers/wallet-portfolio-provider";
import { useRewardPools } from "../../queries/use-reward-pools";
import { useUserDeposits } from "../../queries/use-user-deposits";
import { useTokenInfo } from "../../queries/use-token-info";
import { APP_LAUNCHER_ICONS_BASE, REWARDS_WEB } from "../../constants";
import type { RewardPool } from "../../types";
import { formatPortfolioTokenAmount, formatPortfolioUsd } from "../../utils";
import { SectionCard } from "./SectionCard";
import { ExternalLinkIcon } from "../icons";
import { handleOutlinkClick } from "../../../../lib/outlink";
import BigNumber from "bignumber.js";

const SECTION_TITLE = "ONErewards";

function isPositiveRawAmount(raw: string): boolean {
  return new BigNumber(raw || "0").isGreaterThan(0);
}

export function RewardsSection() {
  const { env, walletAddress, onOutlink } = useWalletPortfolioContext();
  const fiat = usePortfolioFiat();
  const hasAddress = !!walletAddress;
  const { allPools, isLoading: poolsLoading } = useRewardPools(env, hasAddress);
  const { depositMap, isLoading: depositsLoading } = useUserDeposits(
    env,
    walletAddress,
    hasAddress,
  );
  const { getTokenImage } = useTokenInfo(env, hasAddress);

  // 리워드 서비스가 onechain gametoken 토큰 상세(mode=rewards)로 통합돼
  // 기존 `/rewards/<pool>` 딥링크는 pool 세그먼트가 버려진 채 리다이렉트된다.
  // deposit 토큰 상세의 rewards 모드로 직접 보낸다 — withdraw는 withdraw 탭,
  // claim은 기본(deposit) 탭에 Claim 버튼이 있다.
  const getRewardsUrl = (pool: RewardPool, action: "withdraw" | "claim") =>
    `${REWARDS_WEB[env]}/gametoken/tokens/${pool.deposit_token.address}?mode=rewards${
      action === "withdraw" ? "&trade=withdraw" : ""
    }`;

  const sectionIcon = `${APP_LAUNCHER_ICONS_BASE[env]}/reward.png`;

  const visiblePools = allPools.filter((pool) => {
    const userDeposit = depositMap.get(pool.pool_address.toLowerCase());
    const deposited = userDeposit?.deposited_amount ?? "0";
    const claimable = userDeposit?.claimable_reward ?? "0";
    return !(new BigNumber(deposited).isZero() && new BigNumber(claimable).isZero());
  });

  return (
    <SectionCard
        sectionKey="rewards"
        title={SECTION_TITLE}
        icon={sectionIcon}
        isEmpty={visiblePools.length === 0}
        loading={poolsLoading || depositsLoading}
      >
        {visiblePools.map((pool) => {
          const userDeposit = depositMap.get(pool.pool_address.toLowerCase());
          const deposited = userDeposit?.deposited_amount ?? "0";
          const rewardRaw = userDeposit?.claimable_reward ?? "0";
          const withdrawUrl = getRewardsUrl(pool, "withdraw");
          const claimUrl = getRewardsUrl(pool, "claim");
          const depositIcon = getTokenImage(
            pool.deposit_token.address,
            pool.deposit_token.symbol,
          );
          const firstRewardToken = pool.reward_tokens[0];
          const firstRewardIcon = firstRewardToken
            ? getTokenImage(firstRewardToken.address, firstRewardToken.symbol)
            : "";
          const rewardSymbols = pool.reward_tokens.map((rt) => rt.symbol);
          const poolLabel = rewardSymbols.length > 0
            ? `${pool.deposit_token.symbol}-${rewardSymbols.join("/")}`
            : pool.deposit_token.symbol;
          const rewardAmount = firstRewardToken
            ? formatPortfolioTokenAmount(rewardRaw, firstRewardToken.decimals)
            : "0";
          const rewardSymbol = firstRewardToken?.symbol ?? "Reward";
          const depositedUsd = formatPortfolioUsd(
            deposited,
            pool.deposit_token.decimals,
            pool.deposit_token.price,
            fiat,
          );
          const rewardUsd = firstRewardToken
            ? formatPortfolioUsd(
                rewardRaw,
                firstRewardToken.decimals,
                firstRewardToken.price,
                fiat,
              )
            : null;
          const canClaim = !!userDeposit && isPositiveRawAmount(rewardRaw);

          return (
            <article
              className="wp-item"
              data-side="reward"
              key={pool.pool_address}
            >
              <div className="wp-item-body">
                <div className="wp-item-line">
                  <span className="wp-item-label">Pool (Deposit - Earn)</span>
                  <div className="wp-svc-token">
                    <span className="wp-token-stack" aria-hidden>
                      {depositIcon && (
                        <img
                          className="wp-svc-token-icon"
                          src={depositIcon}
                          alt=""
                        />
                      )}
                      {firstRewardIcon && (
                        <img
                          className="wp-svc-token-icon"
                          src={firstRewardIcon}
                          alt=""
                        />
                      )}
                    </span>
                    <span>{poolLabel}</span>
                  </div>
                </div>
                <div className="wp-item-line">
                  <span className="wp-item-label">Deposited</span>
                  <div className="wp-svc-token wp-svc-token--amount">
                    {depositIcon && (
                      <img
                        className="wp-svc-token-icon"
                        src={depositIcon}
                        alt=""
                      />
                    )}
                    <div className="wp-svc-amount">
                      <span className="wp-svc-amount-main">
                        {formatPortfolioTokenAmount(
                          deposited,
                          pool.deposit_token.decimals,
                        )}
                      </span>
                      {depositedUsd && (
                        <span className="wp-item-usd">{depositedUsd}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="wp-item-line">
                  <span className="wp-item-label">
                    Claimable ({rewardSymbol})
                  </span>
                  <div className="wp-svc-token wp-svc-token--amount">
                    {firstRewardIcon && (
                      <img
                        className="wp-svc-token-icon"
                        src={firstRewardIcon}
                        alt=""
                      />
                    )}
                    <div className="wp-svc-amount">
                      <span className="wp-svc-amount-main">{rewardAmount}</span>
                      {rewardUsd && (
                        <span className="wp-item-usd">{rewardUsd}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="wp-item-tags">
                <a
                  className="wp-tag wp-tag--muted"
                  href={withdrawUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-track="rewards_withdraw_open"
                  onClick={(e) =>
                    handleOutlinkClick(
                      e,
                      withdrawUrl,
                      {
                        category: "portfolio",
                        origin: "portfolio-rewards",
                        payload: { pool, userDeposit },
                      },
                      onOutlink,
                    )
                  }
                >
                  <span className="wp-tag-label">Withdraw</span>
                  <ExternalLinkIcon size={12} className="wp-tag-icon" />
                </a>
                <a
                  className="wp-tag wp-tag--mint"
                  data-disabled={!canClaim || undefined}
                  aria-disabled={!canClaim || undefined}
                  href={canClaim ? claimUrl : undefined}
                  target={canClaim ? "_blank" : undefined}
                  rel={canClaim ? "noopener noreferrer" : undefined}
                  onClick={(e) => {
                    if (!canClaim) {
                      e.preventDefault();
                      return;
                    }
                    handleOutlinkClick(
                      e,
                      claimUrl,
                      {
                        category: "portfolio",
                        origin: "portfolio-rewards",
                        payload: { pool, userDeposit },
                      },
                      onOutlink,
                    );
                  }}
                >
                  <span className="wp-tag-label">Claim</span>
                  <ExternalLinkIcon size={12} className="wp-tag-icon" />
                </a>
              </div>
            </article>
          );
        })}
    </SectionCard>
  );
}
